/**
 * Named capture scenarios. ARCHITECTURE.md §18.1.
 *
 * Each scenario is DATA, not a script: seed, fighters, positions, forced states, forced move and
 * frame, camera override, quality preset, and how many warm-up ticks to run so weather and
 * particles reach a steady state.
 *
 * The requirement each one must satisfy: run from a fresh page, it produces an identical frame
 * every time. That is why nothing here reads a wall clock or an RNG stream the simulation shares.
 */

/**
 * @typedef {object} Scenario
 * @property {string} name
 * @property {string} description what a reviewer should be judging in this shot
 * @property {number} seed
 * @property {[string,string]} fighters
 * @property {string} screen
 * @property {[number,number]} [positions] x for P1 and P2
 * @property {number} [depth] z offset applied to P2, for sidestep framing
 * @property {string} [p0Move] move id forced on P1
 * @property {number} [p0Frame] f60 frame to freeze that move at
 * @property {string} [p1Move]
 * @property {number} [p1Frame]
 * @property {string} [p0State] forced state for P1 (reset, bypasses the transition table)
 * @property {string} [p1State]
 * @property {number} [p0StateTick] presentation tick inside the forced P1 state
 * @property {number} [p1StateTick]
 * @property {[number,number,number]} [p0Velocity] forced vx/vy/vz used by state motion
 * @property {[number,number,number]} [p1Velocity]
 * @property {boolean} [p0FaceUp] knockdown orientation
 * @property {boolean} [p1FaceUp]
 * @property {boolean} [p0KnockdownHard]
 * @property {boolean} [p1KnockdownHard]
 * @property {{attacker:number,moveId:string,state:'hold'|'execute'|'release',tick:number,chainIndex?:number}} [pairedThrow]
 * @property {number} [p0Health] 0..1 fraction
 * @property {number} [p1Health]
 * @property {[number,number]} [rounds] round wins shown by result scenarios
 * @property {[number,number]} [bestCombo]
 * @property {[number,number]} [bestComboDamage]
 * @property {number} [timeRemaining] whole seconds shown by result scenarios
 * @property {number} [storm] 0..1 weather ramp
 * @property {number} [warmupTicks] ticks to advance before capturing
 * @property {boolean} [hitboxes] enable the debug overlay
 * @property {object} [camera] locked camera pose
 * @property {string} [preset] quality preset override
 * @property {object} [impact] deterministic preview of the real `fx:impact` event at this pose
 * @property {number} [impactAgeTicks] preview age at the contact pose, so particle direction reads
 */

/** @type {Record<string, Scenario>} */
export const SCENARIOS = {
  'character-select': {
    name: 'character-select',
    description: 'Selection screen hierarchy and legibility. Both fighters presented and readable.',
    seed: 0x5eed0001,
    fighters: ['kestrel', 'bulwark'],
    screen: 'select',
    warmupTicks: 60,
  },

  'round-opening': {
    name: 'round-opening',
    description: 'The first frame of a round: both fighters in their intro pose, HUD fully drawn.',
    seed: 0x5eed0002,
    fighters: ['kestrel', 'bulwark'],
    screen: 'fight',
    positions: [-1.05, 1.05],
    p0State: 'round-intro',
    p1State: 'round-intro',
    storm: 0,
    warmupTicks: 40,
  },

  'neutral-spacing': {
    name: 'neutral-spacing',
    description: 'Mid-range neutral. Judge silhouette separation and whether both fighters read '
      + 'against the background at typical fighting distance.',
    seed: 0x5eed0003,
    fighters: ['kestrel', 'bulwark'],
    screen: 'fight',
    positions: [-1.5, 1.5],
    storm: 0.2,
    warmupTicks: 180,
  },

  'close-guard': {
    name: 'close-guard',
    description: 'Point blank, defender guarding. Judge limb interpenetration and guard pose clarity.',
    seed: 0x5eed0004,
    fighters: ['kestrel', 'bulwark'],
    screen: 'fight',
    positions: [-0.42, 0.42],
    p1State: 'guard-stand',
    p1StateTick: 10,
    storm: 0.25,
    warmupTicks: 120,
  },

  'light-hit': {
    name: 'light-hit',
    description: 'A jab connecting, frozen on the active frame. Judge contact alignment: the fist '
      + 'must be at the hitbox, and the hit spark at the contact point.',
    seed: 0x5eed0005,
    fighters: ['kestrel', 'bulwark'],
    screen: 'fight',
    positions: [-0.5, 0.5],
    p0Move: 'ky-jab',
    p0Frame: 11,
    impact: {
      kind: 'spark-light', x: 0.08, y: 1.38, z: 0, nx: 1, ny: 0, nz: 0,
      strength: 0.72, surface: 'body', scale: 0.82, hue: 0,
    },
    impactAgeTicks: 2,
    storm: 0.3,
    warmupTicks: 100,
  },

  'kestrel-recovery': {
    name: 'kestrel-recovery',
    description: 'Kestrel halfway through Vein Split recovery. Judge the quick guard rebuild, '
      + 'continuous shoulder volume, planted feet, facial focus, and scarf clearance.',
    seed: 0x5eed0015,
    fighters: ['kestrel', 'bulwark'],
    screen: 'fight',
    positions: [-0.72, 0.92],
    p0Move: 'ky-veinsplit',
    p0Frame: 29,
    storm: 0.38,
    warmupTicks: 110,
    camera: { x: 0.04, y: 1.72, z: 5.05, targetX: 0, targetY: 1.06 },
  },

  'bulwark-recovery': {
    name: 'bulwark-recovery',
    description: 'Bulwark halfway through Iron Hammer recovery. Judge the weighted torso settle, '
      + 'bent support legs, continuous shoulder/hip volumes, and slower guard return.',
    seed: 0x5eed0016,
    fighters: ['bulwark', 'kestrel'],
    screen: 'fight',
    positions: [-0.72, 0.92],
    p0Move: 'bw-hammer',
    p0Frame: 39,
    storm: 0.5,
    warmupTicks: 110,
    camera: { x: 0.04, y: 1.72, z: 5.05, targetX: 0, targetY: 1.04 },
  },

  'heavy-counter-hit': {
    name: 'heavy-counter-hit',
    description: 'A counter-hit landing. Judge whether the counter reads as distinct from a normal '
      + 'hit, and whether the camera impulse hides defensive information.',
    seed: 0x5eed0006,
    fighters: ['kestrel', 'bulwark'],
    screen: 'fight',
    positions: [-0.62, 0.62],
    p0Move: 'ky-veinsplit',
    p0Frame: 16,
    p1Move: 'bw-hammer',
    p1Frame: 8,
    impact: {
      kind: 'spark-counter', x: 0.1, y: 1.33, z: 0, nx: 1, ny: 0.08, nz: 0,
      strength: 1.28, surface: 'body', scale: 1.22, hue: 0.55,
    },
    impactAgeTicks: 3,
    storm: 0.45,
    warmupTicks: 100,
  },

  throw: {
    name: 'throw',
    description: 'Hold Iron catch. Judge whether both hands compress the torso, the victim reacts '
      + 'to the bearhug, and the linked participants read as one grapple.',
    seed: 0x5eed0007,
    fighters: ['bulwark', 'kestrel'],
    screen: 'fight',
    positions: [-0.4, 0.4],
    pairedThrow: {
      attacker: 0, moveId: 'bw-holdiron', state: 'hold', tick: 22, chainIndex: 0,
    },
    impact: {
      kind: 'throw-burst', x: 0.02, y: 1.2, z: 0, nx: -1, ny: 0.16, nz: 0,
      strength: 0.86, surface: 'body', scale: 0.9, hue: 0,
    },
    impactAgeTicks: 2,
    storm: 0.4,
    warmupTicks: 100,
  },

  'throw-lift': {
    name: 'throw-lift',
    description: 'Hold Iron chain lift at its apex. Judge the under-body grips, Bulwark brace, '
      + 'Kestrel clearance, silhouette separation, and absence of detached roots.',
    seed: 0x5eed0011,
    fighters: ['bulwark', 'kestrel'],
    screen: 'fight',
    positions: [-0.38, 0.38],
    pairedThrow: {
      attacker: 0, moveId: 'bw-hold-lift', state: 'execute', tick: 28, chainIndex: 1,
    },
    storm: 0.46,
    warmupTicks: 110,
    camera: { x: 0, y: 1.92, z: 5.35, targetX: 0, targetY: 1.22 },
  },

  'throw-slam': {
    name: 'throw-slam',
    description: 'Hold Iron chain end immediately before roof contact. Judge continuous grips, '
      + 'victim orientation, attacker weight transfer, and a clear impending ground impact.',
    seed: 0x5eed0012,
    fighters: ['bulwark', 'kestrel'],
    screen: 'fight',
    positions: [-0.38, 0.38],
    pairedThrow: {
      attacker: 0, moveId: 'bw-hold-slam', state: 'execute', tick: 42, chainIndex: 2,
    },
    impact: {
      kind: 'ground-crack', x: 0.16, y: 0.08, z: 0, nx: 0, ny: 1, nz: 0,
      strength: 1.2, surface: 'wet-concrete', scale: 1.18, hue: 0,
    },
    impactAgeTicks: 1,
    storm: 0.58,
    warmupTicks: 110,
    camera: { x: 0, y: 1.7, z: 5.2, targetX: 0, targetY: 0.92 },
  },

  'throw-release': {
    name: 'throw-release',
    description: 'Hold Iron release follow-through. Judge open pushing hands, Bulwark recovery '
      + 'weight, Kestrel separation into the fall, and the absence of a snap back to neutral.',
    seed: 0x5eed0014,
    fighters: ['bulwark', 'kestrel'],
    screen: 'fight',
    positions: [-0.42, 0.42],
    pairedThrow: {
      attacker: 0, moveId: 'bw-hold-slam', state: 'release', tick: 7, chainIndex: 2,
    },
    storm: 0.48,
    warmupTicks: 110,
    camera: { x: 0, y: 1.72, z: 5.2, targetX: 0, targetY: 0.96 },
  },

  'throw-break': {
    name: 'throw-break',
    description: 'Mutual throw escape. Judge the defender shove, rejected attacker recoil, open '
      + 'hands, grounded separation, and readability without a lingering grapple.',
    seed: 0x5eed0013,
    fighters: ['bulwark', 'kestrel'],
    screen: 'fight',
    positions: [-0.48, 0.48],
    p0State: 'throw-broken',
    p1State: 'throw-tech',
    p0StateTick: 4,
    p1StateTick: 4,
    p0Velocity: [-1.8, 0, 0],
    p1Velocity: [2.4, 0, 0],
    impact: {
      kind: 'throw-burst', x: 0, y: 1.1, z: 0, nx: 1, ny: 0.2, nz: 0,
      strength: 0.7, surface: 'body', scale: 0.8, hue: 0,
    },
    impactAgeTicks: 3,
    storm: 0.36,
    warmupTicks: 100,
    camera: { x: 0, y: 1.72, z: 5.15, targetX: 0, targetY: 1.06 },
  },

  'wall-splat': {
    name: 'wall-splat',
    description: 'Defender pinned against the boundary. Judge whether the wall is legible as a '
      + 'wall and whether the camera frames the interaction.',
    seed: 0x5eed0008,
    fighters: ['bulwark', 'kestrel'],
    screen: 'fight',
    positions: [4.3, 5.1],
    p1State: 'wall-splat',
    p1StateTick: 4,
    impact: {
      kind: 'wall-crack', x: 5.08, y: 1.08, z: 0, nx: -1, ny: 0, nz: 0,
      strength: 1.22, surface: 'concrete', scale: 1.28, hue: 0,
    },
    impactAgeTicks: 4,
    storm: 0.55,
    warmupTicks: 90,
  },

  'arena-wide-rain': {
    name: 'arena-wide-rain',
    description: 'Wide establishing shot with weather at full strength. Judge stage composition, '
      + 'city depth, and whether rain obscures the fighters.',
    seed: 0x5eed0009,
    fighters: ['kestrel', 'bulwark'],
    screen: 'fight',
    positions: [-2.6, 2.6],
    storm: 1,
    warmupTicks: 420,
    camera: { x: 0, y: 3.1, z: 10.5, targetX: 0, targetY: 1.4 },
  },

  'arena-low-angle': {
    name: 'arena-low-angle',
    description: 'Low arena critic view. Judge coherent puddles, runoff seams, rain-ground response, '
      + 'boundary height, midground machinery, and a calm silhouette floor around both fighters.',
    seed: 0x5eed0017,
    fighters: ['kestrel', 'bulwark'],
    screen: 'fight',
    positions: [-2.35, 2.35],
    storm: 0.52,
    warmupTicks: 240,
    camera: { x: 0, y: 1.58, z: 7.75, targetX: 0, targetY: 0.82 },
  },

  'final-round-storm': {
    name: 'final-round-storm',
    description: 'Final-round establishing frame during a deterministic lightning pulse. Judge '
      + 'visible storm escalation, skyline haze layers, rain clarity, and fighter separation.',
    seed: 0x5eed0018,
    fighters: ['kestrel', 'bulwark'],
    screen: 'fight',
    positions: [-2.6, 2.6],
    storm: 1,
    // 504 ticks lands inside the deterministic storm's first paired flash window.
    warmupTicks: 504,
    camera: { x: 0, y: 3.15, z: 10.65, targetX: 0, targetY: 1.42 },
  },

  finisher: {
    name: 'finisher',
    description: 'Cinematic finishing technique at its impact frame. Judge whether the effect '
      + 'reads as decisive without erasing the fighters.',
    seed: 0x5eed000a,
    fighters: ['kestrel', 'bulwark'],
    screen: 'fight',
    positions: [-0.8, 0.8],
    p0Move: 'ky-severance',
    p0Frame: 28,
    p1Health: 0.08,
    impact: {
      kind: 'finisher-shock', x: 0.34, y: 1.36, z: 0, nx: 1, ny: 0.12, nz: 0,
      strength: 1.65, surface: 'body', scale: 1.38, hue: 0.52,
    },
    impactAgeTicks: 3,
    storm: 1,
    warmupTicks: 120,
  },

  victory: {
    name: 'victory',
    description: 'Post-match results. Judge information hierarchy and whether the winner is '
      + 'immediately obvious.',
    seed: 0x5eed000b,
    fighters: ['kestrel', 'bulwark'],
    screen: 'results',
    positions: [-1.2, 1.2],
    p0State: 'match-win',
    p1State: 'match-loss',
    p0StateTick: 42,
    p1StateTick: 42,
    p1Health: 0,
    rounds: [2, 0],
    bestCombo: [6, 2],
    bestComboDamage: [184, 58],
    timeRemaining: 23,
    storm: 0.8,
    warmupTicks: 150,
  },

  'training-hitboxes': {
    name: 'training-hitboxes',
    description: 'Training mode with collision volumes visible. Judge whether the hitbox sits on '
      + 'the limb that is supposed to be striking — a mismatch here is an animation defect.',
    seed: 0x5eed000c,
    fighters: ['kestrel', 'bulwark'],
    screen: 'training',
    positions: [-0.55, 0.55],
    p0Move: 'ky-midkick',
    p0Frame: 16,
    hitboxes: true,
    storm: 0.2,
    warmupTicks: 90,
    camera: { x: 0, y: 1.75, z: 5.4, targetX: 0, targetY: 1.2 },
  },

  'locomotion-step': {
    name: 'locomotion-step',
    description: 'Close movement review. Judge heel-to-toe weight transfer, lifted swing foot, '
      + 'counter-swinging guard, and whether the planted sole stays on the roof.',
    seed: 0x5eed000d,
    fighters: ['kestrel', 'bulwark'],
    screen: 'fight',
    positions: [-0.98, 1.35],
    p0State: 'walk-fwd',
    p0StateTick: 14,
    p0Velocity: [2.35, 0, 0],
    storm: 0.28,
    warmupTicks: 100,
    camera: { x: 0, y: 1.7, z: 5.05, targetX: -0.15, targetY: 1.05 },
  },

  'hit-reaction': {
    name: 'hit-reaction',
    description: 'Defender immediately after a heavy body hit. Judge directional recoil, loose '
      + 'hands, head lag, bent support legs, and a distinct Bulwark weight response.',
    seed: 0x5eed000e,
    fighters: ['kestrel', 'bulwark'],
    screen: 'fight',
    positions: [-0.78, 0.72],
    p0Move: 'ky-veinsplit',
    p0Frame: 18,
    p1State: 'hitstun-stand',
    p1StateTick: 5,
    p1Velocity: [3.4, 0, 0],
    impact: {
      kind: 'spark-heavy', x: 0.18, y: 1.24, z: 0, nx: 1, ny: 0.04, nz: 0,
      strength: 1.12, surface: 'body', scale: 1.08, hue: 0.04,
    },
    impactAgeTicks: 4,
    storm: 0.42,
    warmupTicks: 100,
    camera: { x: 0, y: 1.72, z: 5.2, targetX: 0, targetY: 1.08 },
  },

  knockdown: {
    name: 'knockdown',
    description: 'Hard face-up knockdown. Judge floor contact, readable orientation, splayed limbs, '
      + 'costume silhouette, and the absence of floating feet or a generic standing lean.',
    seed: 0x5eed000f,
    fighters: ['kestrel', 'bulwark'],
    screen: 'fight',
    positions: [-1.25, 0.58],
    p1State: 'knockdown-ground',
    p1StateTick: 10,
    p1FaceUp: true,
    p1KnockdownHard: true,
    storm: 0.5,
    warmupTicks: 120,
    camera: { x: 0, y: 1.95, z: 5.35, targetX: -0.05, targetY: 0.72 },
  },

  wakeup: {
    name: 'wakeup',
    description: 'Neutral wake-up through the braced mid-rise pose. Judge continuous floor-to-guard '
      + 'weight transfer and whether both feet find the ground before control returns.',
    seed: 0x5eed0010,
    fighters: ['kestrel', 'bulwark'],
    screen: 'fight',
    positions: [-1.25, 0.58],
    p1State: 'wakeup-neutral',
    p1StateTick: 9,
    p1FaceUp: true,
    p1KnockdownHard: true,
    storm: 0.5,
    warmupTicks: 120,
    camera: { x: 0, y: 1.82, z: 5.25, targetX: -0.05, targetY: 0.9 },
  },
};

export const SCENARIO_NAMES = Object.freeze(Object.keys(SCENARIOS));

/**
 * The authoritative capture list for `tools/baseline.mjs`, in review order.
 * Kept separate from SCENARIOS so a scenario can exist for debugging without entering the baseline.
 */
export const BASELINE_SHOTS = Object.freeze([
  'character-select',
  'round-opening',
  'neutral-spacing',
  'locomotion-step',
  'close-guard',
  'light-hit',
  'hit-reaction',
  'kestrel-recovery',
  'bulwark-recovery',
  'heavy-counter-hit',
  'throw',
  'throw-lift',
  'throw-slam',
  'throw-release',
  'throw-break',
  'wall-splat',
  'knockdown',
  'wakeup',
  'arena-wide-rain',
  'arena-low-angle',
  'final-round-storm',
  'finisher',
  'victory',
  'training-hitboxes',
]);

export function getScenario(name) {
  const s = SCENARIOS[name];
  if (!s) {
    throw new Error(`Unknown scenario "${name}". Known: ${SCENARIO_NAMES.join(', ')}`);
  }
  return s;
}
